import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { changeFormatOfDate } from 'utils/changeFormatOfDate';
import { FileAttachIcon, InfoIcon, RefreshIcon, SettingsIcon } from 'components/icons';
import { UMButton } from '@urbanmetrix/um-kit';
import { GridNode, ProjectNode } from 'types/project';
import AddGridButton from './components/AddGridButton';
import { deleteIndexedDbsByName, listIndexedDbNames } from 'services/storageCleanup';
import BackArrow from 'components/back-arrow/BackArrow';
import { useNavigate } from 'react-router-dom';
import { formatCubicMeters } from 'utils/formatCubicMeters';

type ProjectInfoTableProps = {
  currentProject?: ProjectNode | null;
  onRefreshProject?: () => void;
  onScrollToFiles?: () => void;
};

type StateStatus = 'available' | 'empty' | 'unavailable' | 'loading';
type StateAvailability = Record<string, StateStatus>;
const AUTOMATIC_STATE_BACKEND_TODO = 'Automatic state APIs are not available on the backend yet.';

const ProjectInfoTable: React.FC<ProjectInfoTableProps> = ({ currentProject, onRefreshProject, onScrollToFiles }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [stateAvailability, setStateAvailability] = useState<StateAvailability>({});
  const [cachedGridIds, setCachedGridIds] = useState<Set<string>>(new Set());
  const [selectedStateGridIds, setSelectedStateGridIds] = useState<Set<string>>(new Set());
  const [selectedCacheGridIds, setSelectedCacheGridIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const gridRows = useMemo(
    () =>
      (currentProject?.grids ?? []).map((grid: GridNode) => ({
        id: String(grid.id),
        name: grid.name || String(grid.id),
      })),
    [currentProject?.grids]
  );

  useEffect(() => {
    if (!isSettingsDialogOpen) return;

    let isCancelled = false;
    const load = async () => {
      setIsLoading(true);
      setMessage(null);
      setSelectedStateGridIds(new Set());
      setSelectedCacheGridIds(new Set());
      setStateAvailability(
        gridRows.reduce<StateAvailability>((acc, grid) => {
          acc[grid.id] = 'unavailable';
          return acc;
        }, {})
      );

      const cachePromise = (async () => {
        try {
          const dbNames = await listIndexedDbNames();
          return new Set(dbNames);
        } catch {
          return new Set<string>();
        }
      })();

      const [cachedNames] = await Promise.all([cachePromise]);
      if (isCancelled) return;

      const nextStateAvailability: StateAvailability = {};
      gridRows.forEach((grid) => {
        // TODO: re-enable state cleanup when backend exposes automatic state APIs again.
        nextStateAvailability[grid.id] = 'unavailable';
      });

      setCachedGridIds(new Set(gridRows.filter((grid) => cachedNames.has(grid.id)).map((grid) => grid.id)));
      setStateAvailability(nextStateAvailability);
      setMessage(AUTOMATIC_STATE_BACKEND_TODO);
      setIsLoading(false);
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, [isSettingsDialogOpen, gridRows]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const toggleStateSelection = (gridId: string) => {
    setSelectedStateGridIds((prev) => {
      const next = new Set(prev);
      if (next.has(gridId)) next.delete(gridId);
      else next.add(gridId);
      return next;
    });
  };

  const toggleCacheSelection = (gridId: string) => {
    setSelectedCacheGridIds((prev) => {
      const next = new Set(prev);
      if (next.has(gridId)) next.delete(gridId);
      else next.add(gridId);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    const stateIds = Array.from(selectedStateGridIds);
    const cacheIds = Array.from(selectedCacheGridIds);
    if (stateIds.length === 0 && cacheIds.length === 0) return;

    setIsDeleting(true);
    setMessage(null);

    let deletedStateCount = 0;
    let failedStateCount = 0;

    if (stateIds.length > 0) {
      // TODO: backend endpoints for deleting automatic states are currently unavailable.
      failedStateCount = stateIds.length;
    }

    let deletedCacheCount = 0;
    let failedCacheCount = 0;
    if (cacheIds.length > 0) {
      const { succeeded, failed } = await deleteIndexedDbsByName(cacheIds);
      deletedCacheCount = succeeded.length;
      failedCacheCount = failed.length;
    }

    if (deletedStateCount > 0) {
      setStateAvailability((prev) => {
        const next = { ...prev };
        stateIds.forEach((id) => {
          next[id] = 'empty';
        });
        return next;
      });
    }
    if (deletedCacheCount > 0) {
      setCachedGridIds((prev) => {
        const next = new Set(prev);
        cacheIds.forEach((id) => next.delete(id));
        return next;
      });
    }

    setSelectedStateGridIds(new Set());
    setSelectedCacheGridIds(new Set());

    const fragments = [];
    if (stateIds.length > 0)
      fragments.push(`state ${deletedStateCount} deleted${failedStateCount > 0 ? `, ${failedStateCount} failed` : ''}`);
    if (cacheIds.length > 0)
      fragments.push(`cache ${deletedCacheCount} deleted${failedCacheCount > 0 ? `, ${failedCacheCount} failed` : ''}`);
    setMessage(`Delete completed: ${fragments.join(' | ')}.`);
    setIsDeleting(false);
  };

  const isDeleteDisabled = selectedStateGridIds.size === 0 && selectedCacheGridIds.size === 0;
  const selectableStateGridIds = gridRows
    .filter((grid) => stateAvailability[grid.id] === 'available')
    .map((grid) => grid.id);
  const selectableCacheGridIds = gridRows.filter((grid) => cachedGridIds.has(grid.id)).map((grid) => grid.id);

  const isAllStateSelected =
    selectableStateGridIds.length > 0 && selectableStateGridIds.every((id) => selectedStateGridIds.has(id));
  const isAllCacheSelected =
    selectableCacheGridIds.length > 0 && selectableCacheGridIds.every((id) => selectedCacheGridIds.has(id));
  const projectLocation = currentProject?.address?.trim() || currentProject?.country || '-';

  const handleToggleSelectAllState = () => {
    setSelectedStateGridIds(isAllStateSelected ? new Set() : new Set(selectableStateGridIds));
  };

  const handleToggleSelectAllCache = () => {
    setSelectedCacheGridIds(isAllCacheSelected ? new Set() : new Set(selectableCacheGridIds));
  };

  const open = Boolean(anchorEl);

  return (
    <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <BackArrow onClick={() => navigate('/dashboard')} aria-label="Back to projects" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ marginRight: 5 }}>{currentProject?.name}</span>
          <InfoIcon onClick={handlePopoverOpen} />
          <FileAttachIcon onClick={onScrollToFiles} />
          <SettingsIcon onClick={() => setIsSettingsDialogOpen(true)} />
          <RefreshIcon onClick={onRefreshProject} />
        </div>
      </div>

      <div style={{ marginLeft: 'auto', marginRight: '50px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <AddGridButton
          projectId={currentProject?.id}
          supportFlagged={currentProject?.supportFlagged}
          style={{ marginRight: -1 }}
        />
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'rgba(97, 97, 97, 0.92)',
              px: 0.5,
              py: 0.5,
            },
          },
        }}
      >
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 1)' }}>
          {projectLocation}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 1)', mt: 0.5 }}>
          {`Used Volume: ${formatCubicMeters(currentProject?.paidVolume ?? 0)} m³`}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 1)', mt: 0.5 }}>
          {`Created: ${changeFormatOfDate(currentProject?.createdAt)}`}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 1)', mt: 0.5 }}>
          {`Owner: ${currentProject?.owner?.name ?? '-'}`}
        </Typography>
      </Popover>

      <Dialog open={isSettingsDialogOpen} onClose={() => setIsSettingsDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>Project Settings</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Grid</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <span>State</span>
                    <Button
                      size="small"
                      onClick={handleToggleSelectAllState}
                      disabled={isLoading || isDeleting || selectableStateGridIds.length === 0}
                    >
                      {isAllStateSelected ? 'Unselect all' : 'Select all'}
                    </Button>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <span>Cache</span>
                    <Button
                      size="small"
                      onClick={handleToggleSelectAllCache}
                      disabled={isLoading || isDeleting || selectableCacheGridIds.length === 0}
                    >
                      {isAllCacheSelected ? 'Unselect all' : 'Select all'}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gridRows.map((grid) => {
                const stateFlag = stateAvailability[grid.id];
                const hasCache = cachedGridIds.has(grid.id);
                const stateDisabled = stateFlag !== 'available' || isLoading || isDeleting;
                const cacheDisabled = !hasCache || isLoading || isDeleting;

                return (
                  <TableRow key={grid.id}>
                    <TableCell>{grid.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip
                          title={
                            stateFlag === 'empty'
                              ? 'No state available'
                              : stateFlag === 'unavailable'
                                ? 'State unavailable'
                                : ''
                          }
                        >
                          <span>
                            <Checkbox
                              checked={selectedStateGridIds.has(grid.id)}
                              disabled={stateDisabled}
                              onChange={() => toggleStateSelection(grid.id)}
                            />
                          </span>
                        </Tooltip>
                        {stateFlag === 'loading' && <CircularProgress size={14} />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={!hasCache ? 'No cache available' : ''}>
                          <span>
                            <Checkbox
                              checked={selectedCacheGridIds.has(grid.id)}
                              disabled={cacheDisabled}
                              onChange={() => toggleCacheSelection(grid.id)}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {message && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {message}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <UMButton text={'Close'} onClick={() => setIsSettingsDialogOpen(false)} />
          <UMButton
            text={isDeleting ? 'Deleting...' : 'Delete selected'}
            variant="outlined"
            disabled={isDeleteDisabled || isDeleting}
            onClick={handleDeleteSelected}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectInfoTable;
